import React, { useState } from "react";
import LanguageLink from "../routing/LanguageLink";
import { motion, AnimatePresence } from "framer-motion";
import ScrollAnimation from "../../hooks/ScrollAnimation";

const FaqSection = () => {
  const [openItem, setOpenItem] = useState(null);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  const faqData = {
    title: "Frequently Asked Questions About U.S. Immigration & Our Platform",
    subtitle:
      "Below you’ll find clear, plain-English answers about how our guided USCIS form experience works—from choosing a form and answering questions, to secure checkout, downloading your ready-to-file PDF, and optionally handing off your case to an attorney or a law firm.",
    cta: {
      text: "Get Started Now",
      description:
        "Need help right away? Our team is available 24/7 to guide you through your next steps.",
    },
    questions: [
      {
        id: 1,
        question: "What immigration forms can you help me with?",
        answer:
          "We support many popular USCIS forms across family, employment, and naturalization categories (for example: I-130, I-485, I-765, I-131, I-864, N-400, and others). Availability can vary over time. Inside the app, you pick your form, and our guided Q&A maps your answers to the correct USCIS fields for that form. Tip: If you’re unsure which form you need, start a free intake and we’ll point you to the most common options.",
      },
      {
        id: 2,
        question: "How does the guided form filling process work?",
        answer:
          "Instead of typing into complex fields, you answer simple questions in plain English. While you progress, we run smart checks for missing or inconsistent details. When you finish, you can securely pay and immediately download a ready-to-file PDF generated from your answers. If you’d like extra assurance, you can request a document review before you submit anything to USCIS.",
      },
      {
        id: 3,
        question: "How much do your services cost?",
        answer:
          "Pricing depends on the specific form and any optional services you choose. The core guided form experience is transparently priced at checkout (no hidden fees). If you request an attorney consultation or review, fees for those services are shown clearly before you confirm. Flexible payment options may be available for certain services. For an exact quote, start your form and view the price summary before you pay.",
      },
      {
        id: 4,
        question: "Can I connect with an immigration lawyer through your platform?",
        answer:
          "Yes—if you want legal help, you can request a licensed immigration attorney or a participating law firm after you generate your form. You’ll see profiles with practice focus and languages where available, then choose who to engage. Communication and payment for those legal services happen securely through the platform. Note: Attorney availability varies by state and case type.",
      },
      {
        id: 5,
        question: "Who can use your services and do I qualify?",
        answer:
          "Anyone seeking help preparing USCIS forms can use the guided experience—individuals, sponsors, families, and employers. The platform itself does not determine eligibility for any immigration benefit; rather, it helps you prepare forms accurately. If you need legal advice on eligibility or strategy, you can request a consultation with a licensed attorney from within your dashboard.",
      },
      {
        id: 6,
        question: "What payment methods do you accept and is checkout secure?",
        answer:
          "You can pay with major debit/credit cards and secure online payment methods shown at checkout. All transactions are encrypted. After payment, you’ll receive a confirmation and can immediately download your filled PDF. Receipts are available in your account. If you add attorney services, those fees are itemized and processed securely as well.",
      },
    ],
  };

  return (
    <section className="bg-gray-50 py-12 lg:py-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.span
            className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-3"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            FAQs
          </motion.span>

          <motion.h2
            className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            {faqData.title}
          </motion.h2>

          <motion.p
            className="text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {faqData.subtitle}
          </motion.p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqData.questions.map((item, index) => (
            <ScrollAnimation key={item.id} direction="up" delay={index * 0.05}>
              <motion.div
                className={`bg-white rounded-lg border-2 transition-all duration-300 ${openItem === item.id ? "border-blue-600 shadow-md" : "border-gray-200"
                  }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1 pr-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${openItem === item.id ? "bg-blue-600" : "bg-gray-200"
                      }`}>
                      <svg className={`w-5 h-5 transition-colors ${openItem === item.id ? "text-white" : "text-gray-500"
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className={`font-semibold transition-colors ${openItem === item.id ? "text-blue-600" : "text-gray-900"
                      }`}>
                      {item.question}
                    </h3>
                  </div>
                  <motion.div
                    animate={{ rotate: openItem === item.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="shrink-0"
                  >
                    <svg className={`w-5 h-5 transition-colors ${openItem === item.id ? "text-blue-600" : "text-gray-400"
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </motion.div>
                </button>

                <AnimatePresence>
                  {openItem === item.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-4">
                        <div className="pl-11 pr-8">
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {item.answer}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-3">
                            Heads-up: We help you prepare forms accurately, but we aren’t your attorneys unless you
                            separately engage one through the platform. For legal advice about your specific situation,
                            consider booking a consultation.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </ScrollAnimation>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-10 text-center bg-blue-600 rounded-xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h3 className="text-xl font-bold text-white mb-2">
            Still Have Questions?
          </h3>
          <p className="text-blue-100 mb-4">
            {faqData.cta.description}
          </p>
          <LanguageLink
            to="/lawyer-ai"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            {faqData.cta.text}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </LanguageLink>
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;
